import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import DashboardLayout from '../../components/dashboard/DashboardLayout'
import PageHeader from '../../components/dashboard/PageHeader'
import Card from '../../components/dashboard/Card'
import LoadingSpinner from '../../components/LoadingSpinner'

export default function ManageRequests() {
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(null)

  useEffect(() => {
    loadRequests()
  }, [])

  const loadRequests = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    const { data: userData } = await supabase
      .from('users')
      .select('org_id')
      .eq('id', user.id)
      .single()

    console.log('Current user org_id:', userData.org_id)

    // Load pending requests with user info and group info
    const { data, error } = await supabase
      .from('assignment_requests')
      .select(`
        *,
        user:user_id (id, name, email),
        group:requested_group_id (id, name, depth, parent_id)
      `)
      .eq('status', 'pending')
      .order('created_at', { ascending: false })

    console.log('Requests data:', data)
    console.log('Requests error:', error)

    if (error) throw error

    // Get all groups to build hierarchy names
    const { data: allGroups } = await supabase
      .from('groups')
      .select('*')
      .eq('org_id', userData.org_id)

    console.log('All groups:', allGroups)

    // Enrich requests with full hierarchy
    const enrichedRequests = data.map(request => {
      const subgroup = request.group
      const group = allGroups.find(g => g.id === subgroup.parent_id)
      const team = group ? allGroups.find(g => g.id === group.parent_id) : null

      return {
        ...request,
        teamName: team?.name || '-',
        groupName: group?.name || '-',
        subgroupName: subgroup?.name || '-'
      }
    })

    console.log('Enriched requests:', enrichedRequests)

    setRequests(enrichedRequests)
  } catch (error) {
    console.error('Load requests error:', error)
  } finally {
    setLoading(false)
  }
}

  const handleApprove = async (requestId, userId, groupId) => {
  if (!confirm('Approve this assignment request?')) return

  setActionLoading(requestId)
  try {
    // Check if user is already in this group
    const { data: existingMember } = await supabase
      .from('group_members')
      .select('id')
      .eq('user_id', userId)
      .eq('group_id', groupId)
      .single()

    if (existingMember) {
      alert('This user is already assigned to this group.')
      setActionLoading(null)
      return
    }

    // Generate QR signature
    const qrSignature = `${userId}-${Date.now()}`

    // Update user: assign role, add QR
    const { error: userError } = await supabase
      .from('users')
      .update({
        role: 'member',
        qr_signature: qrSignature,
        qr_generated_at: new Date().toISOString()
      })
      .eq('id', userId)

    if (userError) throw userError

    // Add to group_members
    const { error: memberError } = await supabase
      .from('group_members')
      .insert({
        user_id: userId,
        group_id: groupId
      })

    if (memberError) throw memberError

    // Get current user for reviewed_by
    const { data: { user: currentUser } } = await supabase.auth.getUser()

    // Update request status
    const { error: requestError } = await supabase
      .from('assignment_requests')
      .update({
        status: 'approved',
        reviewed_at: new Date().toISOString(),
        reviewed_by: currentUser.id
      })
      .eq('id', requestId)

    if (requestError) throw requestError

    alert('Request approved! User has been assigned and given a QR code.')
    await loadRequests()

  } catch (error) {
    console.error('Approve error:', error)
    alert('Failed to approve request: ' + error.message)
  } finally {
    setActionLoading(null)
  }
}

  const handleReject = async (requestId) => {
    const reason = prompt('Reason for rejection (optional):')
    if (reason === null) return // User cancelled

    setActionLoading(requestId)
    try {
      const { error } = await supabase
        .from('assignment_requests')
        .update({
          status: 'rejected',
          reviewed_at: new Date().toISOString(),
          rejection_reason: reason || null
        })
        .eq('id', requestId)

      if (error) throw error

      alert('Request rejected.')
      await loadRequests()

    } catch (error) {
      console.error('Reject error:', error)
      alert('Failed to reject request: ' + error.message)
    } finally {
      setActionLoading(null)
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading requests..." />
  }

  return (
    <DashboardLayout>
      <PageHeader
        title="Assignment Requests"
        description="Review and manage member assignment requests"
      >
        <button
          onClick={loadRequests}
          className="inline-flex items-center gap-2 rounded-lg border bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
          </svg>
          Refresh
        </button>
      </PageHeader>

      <div className="p-6">
        <Card>
          {requests.length === 0 ? (
            <div className="text-center py-12">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-gray-400 mb-4">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="8.5" cy="7" r="4"></circle>
                <line x1="20" y1="8" x2="20" y2="14"></line>
                <line x1="23" y1="11" x2="17" y2="11"></line>
              </svg>
              <p className="text-gray-500 text-lg font-medium">No pending requests</p>
              <p className="text-gray-400 text-sm mt-1">All assignment requests have been reviewed</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="pb-3 text-left font-medium text-gray-500">Member</th>
                    <th className="pb-3 text-left font-medium text-gray-500">Email</th>
                    <th className="pb-3 text-left font-medium text-gray-500">Team</th>
                    <th className="pb-3 text-left font-medium text-gray-500">Group</th>
                    <th className="pb-3 text-left font-medium text-gray-500">Subgroup</th>
                    <th className="pb-3 text-left font-medium text-gray-500">Reason</th>
                    <th className="pb-3 text-left font-medium text-gray-500">Requested</th>
                    <th className="pb-3 text-right font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(request => (
                    <tr key={request.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-3 font-medium text-gray-900">{request.user.name}</td>
                      <td className="py-3 text-gray-500">{request.user.email}</td>
                      <td className="py-3 text-gray-500">{request.teamName}</td>
                      <td className="py-3 text-gray-500">{request.groupName}</td>
                      <td className="py-3 text-gray-500">{request.subgroupName}</td>
                      <td className="py-3 text-gray-500">
                        {request.reason ? (
                          <span className="text-xs">{request.reason}</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-3 text-gray-500">
                        {new Date(request.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleApprove(request.id, request.user_id, request.requested_group_id)}
                            disabled={actionLoading === request.id}
                            className="px-3 py-1.5 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                          >
                            {actionLoading === request.id ? 'Processing...' : 'Approve'}
                          </button>
                          <button
                            onClick={() => handleReject(request.id)}
                            disabled={actionLoading === request.id}
                            className="px-3 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </DashboardLayout>
  )
}